import Timecode from 'smpte-timecode';
import { getUpdatedTimedTranscript, LINE_BREAK_CHARACTER } from './transcriptUtils';
import IntervalTree from 'node-interval-tree';
import type TranscriptWord from './transcriptWord';
import type { Word } from './transcriptWord';
import { millisecondsToHumanReadableTime } from '../util/date';
import { createUuid } from '../util/createUuid';
import type { LineBreakIndex } from './lineBreakMarker';
import { safeArrayAccess } from '../util/safeArrayAccess';

export interface Paragraph {
  words: Word[];
  startTime: number;
  id: string;
}

// taken from smpte-timecode type declaration. They don't export the type
// so not possible to import it without compile errors even though IDE allows it
type FRAMERATE = 23.976 | 24 | 25 | 29.97 | 30 | 50 | 59.94 | 60;

const isValidFrameRate = (val: number | undefined): val is FRAMERATE => {
  if (val === undefined) return false;
  const validFrameRates: FRAMERATE[] = [23.976, 24, 25, 29.97, 30, 50, 59.94, 60];
  return validFrameRates.includes(val as FRAMERATE);
};

export default class TranscriptParagraph implements Paragraph {
  public words: TranscriptWord[];
  public timecode?: string;
  public mediaFrameRate?: number;
  public id: string;
  public lineBreakIndex?: LineBreakIndex;

  private wordIntervalTree: any;

  public cleanup() {
    this.wordIntervalTree = undefined;
  }

  get startTime() {
    return this.words.length ? this.words[0]!.startTime : 0;
  }

  get formattedStartTime() {
    if (this.timecode && this.mediaFrameRate && isValidFrameRate(this.mediaFrameRate)) {
      try {
        const t1 = Timecode(this.timecode, this.mediaFrameRate, false);
        const t2 = Timecode((this.words[0]!.startTime / 1000) * this.mediaFrameRate, this.mediaFrameRate, false);
        return t1.add(t2).toString();
      } catch(err) {
        console.log(`Timecode ${this.timecode} is not supported`);
      }
    }
    return millisecondsToHumanReadableTime(this.startTime);
  }

  get endTime() {
    return this.words.length ? this.words[this.words.length - 1]!.endTime : 0;
  }

  public constructor(words: Word[], timecode?: string, mediaFrameRate?: number, lineBreakIndex?: LineBreakIndex) {
    this.words = words;
    this.timecode = timecode;
    this.mediaFrameRate = mediaFrameRate;
    this.wordIntervalTree = this.buildWordIntervalTree(words);
    this.id = createUuid();
    this.lineBreakIndex = lineBreakIndex;
  }

  public getSubWords(index: number): TranscriptWord[] {
    return this.words.slice(index);
  }

  public getRaw(wordSeparator = ' '): string {
    return this.words.reduce((acc: string, cur: TranscriptWord, curIndex: number) => {
      const currentContent = `${cur.content}${this.lineBreakIndex?.[curIndex] ? ` ${LINE_BREAK_CHARACTER}` : ''}${wordSeparator}`;
      return acc += currentContent;
    }, '').trim();
  }

  public update(editedText: string) {
    this.words = getUpdatedTimedTranscript(this.words, editedText);
    this.wordIntervalTree = this.buildWordIntervalTree(this.words);
  }

  public getWordsByTime(start: number, end: number) {
    return this.wordIntervalTree.search(start, end) as TranscriptWord[];
  }

  private buildWordIntervalTree(words: TranscriptWord[]) {
    const tree = new IntervalTree();
    words.forEach((w: TranscriptWord) => {
      if (w.startTime <= w.endTime) {
        tree.insert(w.startTime, w.endTime, w);
      }
    });
    return tree;
  }
}

export interface SelectedWord {
  timed: TranscriptWord;
  startOffset: number;
  endOffset: number;
}

export const findWordRangeFromParagraph = (selection: any, paragraph: TranscriptParagraph, wordSeparator = ' '): SelectedWord[] => {
  const range = [];
  if (selection) {
    let counter = 0;
    for (const w of paragraph.words) {
      const wordEnd = counter + w.content.length;
      if (selection.startOffset >= counter &&
        selection.startOffset <= wordEnd
        || selection.endOffset > counter
        && selection.endOffset < wordEnd
        || selection.startOffset < counter
        && counter <= selection.endOffset) {
        range.push({ timed: w, startOffset: counter, endOffset: wordEnd });
      }
      counter += w.content.length + wordSeparator.length;
      if (counter > selection.endOffset) { break; }
    }
  }
  return range;
};

export const getCurrentWordOffset = (currentWord: TranscriptWord, paragraph: TranscriptParagraph | undefined, wordSeparator: string = ' '): number => {
  if (!paragraph) {
    return 0;
  }
  const index = paragraph.words.indexOf(currentWord);
  if (index === -1) { return -1; }
  let characterCount = 0;
  let lineBreakCount = 0;
  for (let i = 0; i < index; i += 1) {
    const item = paragraph.words[i];
    if (!item) {
      continue;
    }
    characterCount += item.content.length + wordSeparator.length;
    if (paragraph.lineBreakIndex?.[i]) {
      lineBreakCount += 2;
    }
  }
  return characterCount + lineBreakCount;
};


export const getFirstAndLastWordsFromTimeRange = (timeRange: { start: number, end: number },
  ps: { paragraph: TranscriptParagraph, words: TranscriptWord[]}[]) => {
  if (!ps || ps.length === 0) return;

  const firstParagraph = safeArrayAccess(ps, 0).paragraph;
  const lastParagraph = safeArrayAccess(ps, ps.length - 1).paragraph;

  if (firstParagraph.words.length === 0  || lastParagraph.words.length === 0) return;

  // Find the first word that matches the start timestamp
  let firstWord = firstParagraph.words[0];
  for (let i = 0; i < firstParagraph.words.length; i++) {
    const word = safeArrayAccess(firstParagraph.words, i);
    if (!word) {
      continue;
    }
    if (word.startTime <= timeRange.start) {
      firstWord = word;
    } else {
      break;
    }
  }

  // Find the first word that matches the end timestamp
  let lastWord = lastParagraph.words[lastParagraph.words.length - 1];
  for (let i = lastParagraph.words.length - 1; i >= 0; i--) {
    const word = safeArrayAccess(lastParagraph.words, i);
    if (!word) {
      continue;
    }
    if (word.endTime >= timeRange.end) {
      lastWord = word;
    } else {
      break;
    }
  }

  return {
    firstWord,
    lastWord,
    firstParagraph,
    lastParagraph
  };
};




export const findNextParagraphInTime = (timeInMs: number, paragraphs: TranscriptParagraph[]): TranscriptParagraph | undefined => {
  return paragraphs.find((paragraph: TranscriptParagraph) => paragraph.startTime > timeInMs);
};

export const findNextWordInTime = (timeInMs: number, paragraph: TranscriptParagraph): TranscriptWord | undefined => {
  return paragraph.words.find((word: TranscriptWord) => word.startTime > timeInMs);
};

export const findClosestWordInTime = (timeInMs: number, paragraph: TranscriptParagraph): TranscriptWord => {
  return paragraph.words.reduce((prev: TranscriptWord, curr: any) => {
    return (Math.abs(curr.startTime - timeInMs) <= Math.abs(prev.startTime - timeInMs) ? curr : prev);
  });
};

export const findClosestParagraphInTime = (timeInMs: number, paragraphs: TranscriptParagraph[]): TranscriptParagraph => {
  return paragraphs.reduce((prev: TranscriptParagraph, curr: any) => {
    return (Math.abs(curr.startTime - timeInMs) <= Math.abs(prev.startTime - timeInMs) ? curr : prev);
  });
};
